import React, { useMemo, useState, useCallback } from 'react';
import './Dashboard.css';
import Header from './common/Header';
import Sidebar from './common/Sidebar';
import { useFetch } from '../hooks/useFetch';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

// Deterministic accent color per tag (aligned with Expenses chips style)
const tagHue = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
};
const getTagColor = (tag) => `hsl(${tagHue(String(tag))}, 70%, 50%)`;

export default function Tags() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'es';
  const locale = lang.startsWith('es') ? 'es-CL' : lang.startsWith('en') ? 'en-US' : lang;
  const CURRENCY = 'CLP';

  // Month label helper (YYYY-MM -> "Mes Año" según locale)
  const formatMonthLabel = (ym, loc) => {
    if (!ym || ym === 'all') return t('tags.month.all', 'Todos los meses');
    const [y, m] = ym.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString(loc || locale, { month: 'long', year: 'numeric' });
  };

  // Trae todos los gastos
  const [expensesData, refetchExpenses] = useFetch('/transactions', { defaultValue: [], hasPaging: true, defaultLimit: 500 });
  const expenses = useMemo(() => {
    if (Array.isArray(expensesData.transactions)) {
      return expensesData.transactions;
    }
    if (Array.isArray(expensesData)) {
      return expensesData;
    }
    return [];
  }, [expensesData]);

  // Build distinct months from expenses in YYYY-MM format
  const months = useMemo(() => {
    const set = new Set();
    (expenses || []).forEach((e) => {
      const d = new Date(e.date);
      if (!isNaN(d)) {
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        set.add(ym);
      }
    });
    return Array.from(set).sort().reverse(); // latest first
  }, [expenses]);

  // Selected month filter ("all" = no filter)
  const [selectedMonth, setSelectedMonth] = useState('all');

  // Optional range filter (takes precedence over single-month when any bound is set)
  const [fromMonth, setFromMonth] = useState('all');
  const [toMonth, setToMonth] = useState('all');

  const hasRange = fromMonth !== 'all' || toMonth !== 'all';
  const inYmRange = useCallback((ym) => {
    if (!hasRange) return true;
    if (fromMonth !== 'all' && ym < fromMonth) return false;
    return !(toMonth !== 'all' && ym > toMonth);
  }, [hasRange, fromMonth, toMonth]);

  // ---- Controls state ----
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('amount'); // amount | count | name
  const [sortDir, setSortDir] = useState('desc');   // asc | desc
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkRemoveSelectedTag = async () => {
    if (!selectedTag) return;
    if (!modalTagExpenses || modalTagExpenses.length === 0) return;
    const ok = window.confirm(t('tags.confirmBulkRemove', '¿Quitar esta etiqueta de todos los gastos listados?'));
    if (!ok) return;
    try {
      setBulkLoading(true);
      await Promise.all(
        modalTagExpenses.map((e) =>
          api.delete(`/expense/${e.id}/tags`, { data: { tag: selectedTag } })
        )
      );
    } catch (err) {
      console.error('Bulk remove tag failed', err);
      alert(t('tags.errorBulk', 'No se pudieron eliminar todas las etiquetas. Intenta de nuevo.'));
    } finally {
      setBulkLoading(false);
      setSelectedTag(null);
      if (typeof refetchExpenses === 'function') {
        try { await refetchExpenses(); } catch (_) {}
      }
    }
  };
  

  // Modal: selected tag to show expenses
  const [selectedTag, setSelectedTag] = useState(null);

  // Close on ESC
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedTag(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Normalize tags from different back-end shapes
  const extractTags = (expense) => {
    if (!expense) return [];
    // common places we’ve used
    const raw = expense.tags ?? expense.data?.tags ?? expense.data?.tags_csv ?? expense.data?.tagsCsv ?? expense.data?.tagsCSV;
    let arr = [];
    if (Array.isArray(raw)) {
      arr = raw;
    } else if (typeof raw === 'string') {
      arr = raw.split(',');
    }
    return arr.map((t) => String(t).trim()).filter(Boolean);
  };

  // ---- Build tag aggregates ----
  const { rows, totalAmount, totalCount } = useMemo(() => {
    const map = new Map(); // tag -> { amount, count }
    let grandAmount = 0;
    let grandCount = 0;

    expenses.forEach((e) => {
      const d = new Date(e.date);
      const ym = isNaN(d) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (hasRange) {
        if (!inYmRange(ym)) return; // outside range
      } else if (selectedMonth !== 'all') {
        if (ym !== selectedMonth) return; // not the selected single month
      }

      const tags = extractTags(e);
      const amount = Number(e.amount) || 0;
      if (!amount || !Array.isArray(tags) || tags.length === 0) return;

      // Distribuye el monto del gasto entre sus etiquetas para evitar doble conteo
      const perTag = amount / tags.length;

      // El total de la tabla de etiquetas debe ser la suma de los gastos etiquetados
      grandAmount += amount;

      tags.forEach((tag) => {
        if (!tag) return;
        const current = map.get(tag) || { amount: 0, count: 0 };
        current.amount += perTag;
        current.count += 1;
        map.set(tag, current);
        grandCount += 1; // total de usos de etiquetas
      });
    });

    const list = Array.from(map.entries()).map(([tag, v]) => ({ tag, amount: v.amount, count: v.count }));
    return { rows: list, totalAmount: grandAmount, totalCount: grandCount };
  }, [expenses, selectedMonth, hasRange, inYmRange]);

  // ---- Filtering & sorting ----
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = rows;
    if (q) arr = arr.filter((r) => r.tag.toLowerCase().includes(q));

    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      if (sortKey === 'amount') return (a.amount - b.amount) * dir;
      if (sortKey === 'count') return (a.count - b.count) * dir;
      return a.tag.localeCompare(b.tag) * dir;
    };
    return [...arr].sort(cmp);
  }, [rows, query, sortKey, sortDir]);

  const modalTagExpenses = useMemo(() => {
    if (!selectedTag) return [];
    return (expenses || []).filter((e) => {
      const d = new Date(e.date);
      const ym = isNaN(d) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (hasRange) {
        if (!inYmRange(ym)) return false;
      } else if (selectedMonth !== 'all') {
        if (ym !== selectedMonth) return false;
      }
      const tags = extractTags(e);
      return Array.isArray(tags) && tags.includes(selectedTag);
    });
  }, [selectedTag, expenses, hasRange, selectedMonth, inYmRange]);

  // ---- UI helpers ----
  const fmtMoney = (n) => (Number(n) || 0).toLocaleString(locale, { style: 'currency', currency: CURRENCY });
  const fmtInt = (n) => (Number(n) || 0).toLocaleString(locale);

  return (
    <div className="dashboard-container overview">
      <Header title={t('tags.title', 'Etiquetas')} />
      <div className="dashboard-four-columns">
        <Sidebar />
        <div className="dashboard-main">
          <div className="dashboard-section section-wide" style={{ gridColumn: '1 / -1' }}>
            <h2 className="dashboard-section-title" style={{ fontSize: '1.2rem', margin: 0 }}>
              {t('tags.title', 'Etiquetas')}
            </h2>
            <p style={{ margin: '0.5rem 0 0.75rem', opacity: 0.85 }}>
              {t('tags.subtitle', 'Revisa cuánto estás gastando por cada etiqueta y cuán frecuentes son en tus gastos.')}
            </p>

            {/* Active period badge */}
            <div style={{ marginTop: 6 }}>
              {hasRange ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 999,
                  background: 'rgba(92,231,255,0.15)', color: '#055',
                  border: '1px solid rgba(92,231,255,0.35)', fontSize: 12
                }}>
                  📅 {t('tags.period', 'Periodo')}: {fromMonth !== 'all' ? formatMonthLabel(fromMonth, locale) : t('tags.month.all', 'Todos los meses')} → {toMonth !== 'all' ? formatMonthLabel(toMonth, locale) : t('tags.month.all', 'Todos los meses')}
                </span>
              ) : (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 999,
                  background: 'rgba(92,231,255,0.15)', color: '#055',
                  border: '1px solid rgba(92,231,255,0.35)', fontSize: 12
                }}>
                  📅 {t('tags.period', 'Periodo')}: {selectedMonth === 'all' ? t('tags.month.all', 'Todos los meses') : formatMonthLabel(selectedMonth, locale)}
                </span>
              )}
            </div>

            {/* Controls (styled like Expenses filters) */}
            <div className="dashboard-filters-row inside-bar" style={{ alignSelf: 'stretch' }}>
              {/* Search */}
              <div className="search-input-container embedded">
                <input
                  type="text"
                  placeholder={t('tags.search', 'Buscar etiqueta...')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="expense-search-input"
                />
                {query && (
                  <button
                    type="button"
                    className="clear-search-button"
                    onClick={() => setQuery('')}
                    title={t('common.clear', 'Limpiar')}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Single month filter */}
              <div className="dashboard-cardtype-filter">
                <label className="dashboard-cardtype-label">{t('tags.month.label', 'Mes')}:</label>
                <select
                  className="dashboard-cardtype-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <option value="all">{t('tags.month.all', 'Todos los meses')}</option>
                  {months.map((ym) => (
                    <option key={ym} value={ym}>{formatMonthLabel(ym, locale)}</option>
                  ))}
                </select>
              </div>

              {/* Range filter: From */}
              <div className="dashboard-cardtype-filter">
                <label className="dashboard-cardtype-label">{t('tags.range.from', 'Desde')}:</label>
                <select
                  className="dashboard-cardtype-select"
                  value={fromMonth}
                  onChange={(e) => setFromMonth(e.target.value)}
                >
                  <option value="all">{t('tags.month.all', 'Todos los meses')}</option>
                  {months.map((ym) => (
                    <option key={`from-${ym}`} value={ym}>{formatMonthLabel(ym, locale)}</option>
                  ))}
                </select>
              </div>

              {/* Range filter: To */}
              <div className="dashboard-cardtype-filter">
                <label className="dashboard-cardtype-label">{t('tags.range.to', 'Hasta')}:</label>
                <select
                  className="dashboard-cardtype-select"
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value)}
                >
                  <option value="all">{t('tags.month.all', 'Todos los meses')}</option>
                  {months.map((ym) => (
                    <option key={`to-${ym}`} value={ym}>{formatMonthLabel(ym, locale)}</option>
                  ))}
                </select>
              </div>

              {/* Sort controls */}
              <div className="dashboard-cardtype-filter">
                <label className="dashboard-cardtype-label">{t('tags.sortBy', 'Ordenar por')}:</label>
                <select
                  className="dashboard-cardtype-select"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  <option value="amount">{t('tags.sort.amount', 'Monto total')}</option>
                  <option value="count">{t('tags.sort.count', 'Usos')}</option>
                  <option value="name">{t('tags.sort.name', 'Nombre')}</option>
                </select>
              </div>
              <button
                type="button"
                className="pretty-toggle"
                onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                title={t('tags.sort.toggle', 'Invertir orden')}
              >
                {sortDir === 'asc' ? '▲' : '▼'}
              </button>
            </div>

            {/* Empty state */}
            {!filtered.length && (
              <div style={{ marginTop: '0.75rem', opacity: 0.8 }}>
                {query
                  ? t('tags.noResults', 'No hay etiquetas que coincidan con tu búsqueda.')
                  : t('tags.noTags', 'Aún no hay etiquetas registradas en tus gastos.')}
              </div>
            )}

            {/* Table */}
            {filtered.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div
                  className="dashboard-table-wrapper"
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.08)',
                  }}
                >
                  <table className="dashboard-expenses-table" style={{ width: '100%', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>{t('tags.name', 'Etiqueta')}</th>
                        <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{t('tags.count', 'Usos')}</th>
                        <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{t('tags.amount', 'Monto total')}</th>
                        <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>{t('tags.share', '% del total')}</th>
                        <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{t('tags.avg', 'Promedio/uso')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row) => {
                        const share = totalAmount > 0 ? row.amount / totalAmount : 0;
                        const avg = row.count ? row.amount / row.count : 0;
                        return (
                          <tr key={row.tag} onClick={() => setSelectedTag(row.tag)} style={{ cursor: 'pointer' }}>
                            <td>
                              <span
                                className="expense-tag"
                                title={t('tags.openInline', 'Ver gastos con esta etiqueta')}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  border: '1px solid rgba(0,0,0,0.08)',
                                  borderRadius: 6,
                                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                  color: '#333',
                                  padding: '3px 8px',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  lineHeight: 1.2,
                                  maxWidth: '100%'
                                }}
                              >
                                <span
                                  aria-hidden
                                  style={{
                                    display: 'inline-block',
                                    width: 4,
                                    height: 14,
                                    backgroundColor: getTagColor(row.tag),
                                    borderRadius: 2
                                  }}
                                />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.tag}</span>
                              </span>
                            </td>
                            <td style={{ textAlign: 'right', opacity: 0.9 }}>{fmtInt(row.count)}</td>
                            <td style={{ textAlign: 'right', opacity: 0.9 }}>{fmtMoney(row.amount)}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div aria-hidden style={{ height: 8, width: '100%', minWidth: 80, background: 'rgba(0,0,0,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${Math.min(100, Math.round(share * 100))}%`, background: 'linear-gradient(90deg, rgba(92,231,255,0.55), rgba(92,231,255,0.9))' }} />
                                </div>
                                <div style={{ width: 46, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Math.round(share * 100)}%</div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'right', opacity: 0.9 }}>{fmtMoney(avg)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ fontWeight: 600 }}>{t('tags.total', 'Total')}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtInt(totalCount)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(totalAmount)}</td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
            {selectedTag && (
              <div
                className="expense-popup-overlay"
                style={{ zIndex: 1000 }}
                onClick={() => setSelectedTag(null)}
              >
                <div
                  className="expense-popup"
                  style={{ position: 'relative', zIndex: 1001, maxWidth: 900, width: '90%', maxHeight: '80vh', overflow: 'auto' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                    <h3 style={{ margin: 0 }}>{t('tags.expensesFor', 'Gastos con etiqueta')}: “{selectedTag}”</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        type="button"
                        onClick={bulkRemoveSelectedTag}
                        disabled={bulkLoading}
                        title={t('tags.removeAll', 'Quitar etiqueta de todos los gastos mostrados')}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid rgba(200,0,0,0.25)',
                          background: bulkLoading ? 'rgba(200,0,0,0.08)' : 'rgba(200,0,0,0.12)',
                          color: '#700',
                          cursor: bulkLoading ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {bulkLoading ? t('tags.removing', 'Eliminando…') : t('tags.removeAll', 'Quitar etiqueta de todos')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedTag(null)}
                        aria-label={t('common.close', 'Cerrar')}
                        style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {modalTagExpenses.length === 0 ? (
                    <div style={{ opacity: 0.7 }}>{t('tags.noExpensesForTag', 'No hay gastos para esta etiqueta en el periodo seleccionado.')}</div>
                  ) : (
                    <table className="dashboard-expenses-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left' }}>{t('tags.col.date', 'Fecha')}</th>
                          <th style={{ textAlign: 'left' }}>{t('tags.col.description', 'Descripción')}</th>
                          <th style={{ textAlign: 'left' }}>{t('tags.col.installment', 'Cuotas')}</th>
                          <th style={{ textAlign: 'right' }}>{t('tags.col.amount', 'Monto')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalTagExpenses.map((e) => (
                          <tr key={e.id}>
                            <td>
                              {(() => {
                                const d = new Date(e.date);
                                return isNaN(d) ? e.date : d.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
                              })()}
                            </td>
                            <td>{e.description}</td>
                            <td>{e.installment || e.data?.installment || ''}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoney(e.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
    </div>
  );
}